import type { Area } from 'react-easy-crop';

export function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));

    // No forzar crossOrigin sobre blob:/data: generados localmente.
    // En algunos navegadores puede volver inestable la lectura en canvas.
    if (/^https?:\/\//i.test(url)) {
      image.crossOrigin = 'anonymous';
    }

    image.src = url;
  });
}

function sanitizeCrop(pixelCrop: Area, image: HTMLImageElement): Area {
  const naturalWidth = image.naturalWidth || image.width;
  const naturalHeight = image.naturalHeight || image.height;

  const x = Math.max(0, Math.min(pixelCrop.x, naturalWidth - 1));
  const y = Math.max(0, Math.min(pixelCrop.y, naturalHeight - 1));
  const width = Math.max(1, Math.min(pixelCrop.width, naturalWidth - x));
  const height = Math.max(1, Math.min(pixelCrop.height, naturalHeight - y));

  return { x, y, width, height };
}

export async function getCroppedImageBlob(
  imageSrc: string,
  pixelCrop: Area,
  outputWidth: number,
  outputHeight: number,
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No se pudo crear el contexto de canvas para recortar la imagen.');
  }

  canvas.width = outputWidth;
  canvas.height = outputHeight;

  const safeCrop = sanitizeCrop(pixelCrop, image);

  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    image,
    safeCrop.x,
    safeCrop.y,
    safeCrop.width,
    safeCrop.height,
    0,
    0,
    outputWidth,
    outputHeight,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('No se pudo generar el blob de la imagen recortada.'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.92,
    );
  });
}

export function revokeObjectUrl(url: string | null | undefined) {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}
